import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolutionAPI, bodyAPI, exportAPI } from '../api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  Search, Plus, Filter, Loader2, Award, Calendar, 
  FileText, MoreVertical, ChevronRight, Download, Tag, Users, Vote, History
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import VersionHistory from '../components/VersionHistory';

const Resolutions = () => {
  const [resolutions, setResolutions] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versionTarget, setVersionTarget] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter, categoryFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;

      const [resolutionsRes, bodiesRes] = await Promise.all([
        resolutionAPI.getAll(params),
        bodyAPI.getAll(),
      ]);

      setResolutions(resolutionsRes.data);
      setBodies(bodiesRes.data);
    } catch (err) {
      console.error('Failed to fetch resolutions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchResolutions = fetchData;

  const getBodyName = (bodyId) => {
    const body = bodies.find(b => b.id === bodyId);
    return body?.name || 'Unknown';
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-amber-100 text-amber-800 border-amber-300',
      under_review: 'bg-blue-100 text-blue-800 border-blue-300',
      for_approval: 'bg-cyan-100 text-cyan-800 border-cyan-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const getCategoryColor = (category) => {
    const colors = {
      corporate_actions: 'bg-purple-100 text-purple-800',
      finance: 'bg-green-100 text-green-800',
      hr: 'bg-blue-100 text-blue-800',
      governance: 'bg-orange-100 text-orange-800',
      contracts: 'bg-cyan-100 text-cyan-800',
      others: 'bg-gray-100 text-gray-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const filteredResolutions = resolutions.filter(resolution =>
    resolution.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resolution.resolution_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resolutions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Resolutions Register</h1>
          <p className="text-[#5A6570] mt-1">Track and manage all board and stockholder resolutions</p>
        </div>
        <Link to="/resolutions/new">
          <Button className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="create-resolution-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Resolution
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input
            placeholder="Search resolutions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-[#E2E6EA]"
            data-testid="search-resolutions-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[180px] border-[#E2E6EA]" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-2 text-[#5A6570]" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="for_approval">For Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full md:w-[180px] border-[#E2E6EA]" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="corporate_actions">Corporate Actions</SelectItem>
            <SelectItem value="finance">Finance</SelectItem>
            <SelectItem value="hr">HR</SelectItem>
            <SelectItem value="governance">Governance</SelectItem>
            <SelectItem value="contracts">Contracts</SelectItem>
            <SelectItem value="others">Others</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Resolutions List */}
      {filteredResolutions.length === 0 ? (
        <Card className="border-[#E2E6EA]">
          <CardContent className="py-16 text-center">
            <Award className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
            <h3 className="text-lg font-medium text-[#1A1D21] mb-2">No resolutions found</h3>
            <p className="text-[#5A6570] mb-4">
              {searchTerm ? 'Try adjusting your search or filters' : 'Resolutions are created from meeting decisions'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredResolutions.map((resolution) => (
            <Card 
              key={resolution.id} 
              className="border-[#E2E6EA] hover:border-[#00A3A3] transition-colors cursor-pointer"
              data-testid={`resolution-card-${resolution.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="p-2 bg-[#F58A26]/10 rounded-lg">
                      <Award className="h-6 w-6 text-[#F58A26]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <span className="text-sm font-mono text-[#00A3A3]">{resolution.resolution_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${getStatusColor(resolution.status)}`}>
                          {resolution.status.replace('_', ' ')}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#1A1D21] mb-2">{resolution.title}</h3>
                      
                      <div className="flex flex-wrap items-center gap-3 text-sm text-[#5A6570]">
                        <span className={`text-xs px-2 py-0.5 rounded ${getCategoryColor(resolution.category)}`}>
                          {resolution.category.replace('_', ' ')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {format(parseISO(resolution.created_at), 'MMM d, yyyy')}
                        </span>
                        <span>{getBodyName(resolution.body_id)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                          resolution.approval_method === 'referendum' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {resolution.approval_method === 'referendum' ? <Vote className="h-3 w-3" /> : <Users className="h-3 w-3" />}
                          {resolution.approval_method === 'referendum' ? 'By Referendum' : 'During Meeting'}
                        </span>
                      </div>

                      {resolution.tags && resolution.tags.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {resolution.tags.map((tag, i) => (
                            <span 
                              key={i}
                              className="text-xs px-2 py-0.5 rounded bg-[#00A3A3]/10 text-[#00A3A3]"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* SEC Filing Tags */}
                      {resolution.filing_tags && resolution.filing_tags.length > 0 && (
                        <div className="flex gap-1.5 mt-1">
                          {resolution.filing_tags.map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs text-[#F58A26] border-[#F58A26]/40 bg-[#F58A26]/5">
                              <Tag className="h-3 w-3 mr-1" />{tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Details</DropdownMenuItem>
                        <DropdownMenuItem>Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setVersionTarget(resolution.id); setVersionHistoryOpen(true); }}>
                          <History className="h-4 w-4 mr-2" />Version History
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await resolutionAPI.autoTag(resolution.id);
                            if (res.data.auto_detected.length > 0) {
                              toast.success(`Auto-detected SEC tags: ${res.data.auto_detected.join(', ')}`);
                              fetchResolutions();
                            } else {
                              toast.info('No SEC filing tags detected');
                            }
                          } catch (err) { toast.error('Auto-tag failed'); }
                        }}>
                          <Tag className="h-4 w-4 mr-2" />Auto-Tag SEC Filing
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={exportAPI.resolutionPdf(resolution.id)} target="_blank" rel="noopener noreferrer">
                            <Download className="h-4 w-4 mr-2" />Export PDF
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem>Generate Certificate</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <ChevronRight className="h-5 w-5 text-[#A8B1B9]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {/* Version History */}
      <VersionHistory
        entityType="resolution"
        entityId={versionTarget}
        isOpen={versionHistoryOpen}
        onClose={() => { setVersionHistoryOpen(false); setVersionTarget(null); }}
        onRevert={fetchData}
      />
    </div>
  );
};

export default Resolutions;
